import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { DESTINATIONS, TEXTURE_NAVY } from "@/data/homeMedia";

export const DestinationSlider = () => {
  const [active, setActive] = useState(0);
  const paused = useRef(false);

  useEffect(() => {
    const id = setInterval(() => {
      if (!paused.current) setActive((i) => (i + 1) % DESTINATIONS.length);
    }, 7000);
    return () => clearInterval(id);
  }, []);

  const go = (dir) =>
    setActive((i) => (i + dir + DESTINATIONS.length) % DESTINATIONS.length);

  return (
    <section
      className="mt-24 md:mt-32 relative overflow-hidden"
      data-testid="destination-slider"
      onMouseEnter={() => (paused.current = true)}
      onMouseLeave={() => (paused.current = false)}
    >
      <div
        className="absolute inset-0 bg-[#0A1D3A] bg-cover bg-center"
        style={{ backgroundImage: `url(${TEXTURE_NAVY})` }}
      />
      <div className="absolute inset-0 bg-[#0A1D3A]/80" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24">
        {DESTINATIONS.map((d, i) =>
          i !== active ? null : (
            <div
              key={d.id}
              className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center slide-in"
              data-testid={`destination-slide-${d.id}`}
            >
              <div className="md:col-span-6 min-w-0">
                <p className="text-[11px] uppercase tracking-[0.34em] text-[#E4C67A]">{d.eyebrow}</p>
                <h2 className="font-display text-4xl sm:text-5xl text-[#FCFAF6] mt-5">{d.title}</h2>
                <p className="text-base text-[#FCFAF6]/80 leading-relaxed mt-6 max-w-xl">{d.body}</p>
                <ul className="mt-7 space-y-3 max-w-xl">
                  {d.points.map((p) => (
                    <li key={p} className="flex gap-3 text-sm text-[#FCFAF6]/75 leading-relaxed">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 bg-[#E4C67A]" />
                      {p}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap gap-4 mt-9">
                  <Link
                    to={d.primary.to}
                    className="inline-flex items-center gap-3 bg-[#E4C67A] text-[#0F2A52] px-7 py-4 text-sm hover:-translate-y-1 hover:shadow-2xl transition-transform duration-200"
                    data-testid={`destination-cta-primary-${d.id}`}
                  >
                    {d.primary.label} <ArrowRight size={16} />
                  </Link>
                  <Link
                    to={d.secondary.to}
                    className="inline-flex items-center gap-3 border border-[#FCFAF6]/40 text-[#FCFAF6] px-7 py-4 text-sm hover:bg-[#FCFAF6]/10 transition-colors duration-200"
                    data-testid={`destination-cta-secondary-${d.id}`}
                  >
                    {d.secondary.label}
                  </Link>
                </div>
              </div>

              <div className="md:col-span-6 min-w-0">
                <div className="overflow-hidden h-[240px] sm:h-[320px] md:h-[420px] tile-hover group">
                  <img
                    src={d.image}
                    alt={d.imageAlt}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="text-[11px] text-[#FCFAF6]/50 mt-3">{d.caption}</p>
              </div>
            </div>
          )
        )}

        <div className="flex items-center gap-5 mt-12 md:mt-14">
          <button
            type="button"
            onClick={() => go(-1)}
            aria-label="Previous destination"
            className="h-11 w-11 grid place-items-center border border-[#FCFAF6]/35 text-[#FCFAF6] hover:bg-[#E4C67A] hover:text-[#0F2A52] hover:border-[#E4C67A] transition-colors duration-300"
            data-testid="destination-prev"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            onClick={() => go(1)}
            aria-label="Next destination"
            className="h-11 w-11 grid place-items-center border border-[#FCFAF6]/35 text-[#FCFAF6] hover:bg-[#E4C67A] hover:text-[#0F2A52] hover:border-[#E4C67A] transition-colors duration-300"
            data-testid="destination-next"
          >
            <ChevronRight size={18} />
          </button>

          <div className="flex items-center gap-3 ml-2">
            {DESTINATIONS.map((d, i) => (
              <button
                key={d.id}
                type="button"
                onClick={() => setActive(i)}
                aria-label={`Show ${d.title}`}
                className={`h-[3px] transition-all duration-300 ${
                  i === active ? "w-12 bg-[#E4C67A]" : "w-6 bg-[#FCFAF6]/30 hover:bg-[#FCFAF6]/60"
                }`}
                data-testid={`destination-dot-${d.id}`}
              />
            ))}
          </div>

          <p className="ml-auto text-[11px] uppercase tracking-[0.28em] text-[#FCFAF6]/55">
            {String(active + 1).padStart(2, "0")} / {String(DESTINATIONS.length).padStart(2, "0")}
          </p>
        </div>
      </div>
    </section>
  );
};

export default DestinationSlider;
